import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo, useRef } from "react";
import { Call, Device } from "@twilio/voice-sdk";
import { IncomingCallEvent } from "@/model/interface/IncomingCallEvent";

interface Position {
  x: number;
  y: number;
}

export interface ActiveCallInfo {
  callId: string;
  displayName: string;
  contactId?: string | null;
  event?: IncomingCallEvent | null;
  startedAt: number;
}

interface CallDialogContextType {
  isCallDialogOpen: boolean;
  openRequestId: number;
  dialogPosition: Position | null;
  triggerPosition: Position | null;
  openCallDialog: (triggerPos?: Position) => void;
  closeCallDialog: () => void;
  requestCloseCallDialog: () => void;
  registerCloseHandler: (fn: (() => void) | null) => void;
  toggleCallDialog: () => void;
  setDialogPosition: (position: Position) => void;
  incomingCall: Call | null;
  incomingCallEvent: IncomingCallEvent | null;
  setIncomingCall: (call: Call | null, event: IncomingCallEvent | null) => void;
  activeCall: Call | null;
  setActiveCall: (call: Call | null) => void;
  activeCallInfo: ActiveCallInfo | null;
  setActiveCallInfo: (info: ActiveCallInfo | null) => void;
  switchToDialerTab: () => void;
  onSwitchToDialerTab: (() => void) | null;
  registerDialerTabSwitch: (fn: () => void) => void;
  pendingDialNumber: string | null;
  setPendingDialNumber: (number: string | null) => void;
  dialerDraft: string;
  setDialerDraft: (value: string) => void;
  selectedCallerId: string;
  setSelectedCallerId: (phoneNumberId: string) => void;
  heldCall: { call: Call; event: IncomingCallEvent; callId: string } | null;
  setHeldCall: (held: { call: Call; event: IncomingCallEvent; callId: string } | null) => void;
  device: Device | null;
  setDevice: (device: Device | null) => void;
}

const CallDialogContext = createContext<CallDialogContextType | undefined>(undefined);

export function CallDialogProvider({ children }: { children: ReactNode }) {
  const [isCallDialogOpen, setIsCallDialogOpen] = useState(() => {
    const saved = sessionStorage.getItem("callDialogOpen");
    return saved === "true";
  });

  const [dialogPosition, setDialogPositionState] = useState<Position | null>(() => {
    const saved = localStorage.getItem("callDialogPosition");
    return saved ? JSON.parse(saved) : null;
  });

  const [incomingCall, setIncomingCallState] = useState<Call | null>(null);
  const [incomingCallEvent, setIncomingCallEventState] = useState<IncomingCallEvent | null>(null);
  const [activeCall, setActiveCallState] = useState<Call | null>(null);
  const setActiveCall = useCallback((call: Call | null) => {
    setActiveCallState(call);
  }, []);

  const [activeCallInfo, setActiveCallInfoState] = useState<ActiveCallInfo | null>(null);
  const setActiveCallInfo = useCallback((info: ActiveCallInfo | null) => {
    setActiveCallInfoState(info);
  }, []);

  const [heldCall, setHeldCallState] = useState<{ call: Call; event: IncomingCallEvent; callId: string } | null>(null);
  const setHeldCall = useCallback((held: { call: Call; event: IncomingCallEvent; callId: string } | null) => {
    setHeldCallState(held);
  }, []);

  const [device, setDeviceState] = useState<Device | null>(null);
  const setDevice = useCallback((d: Device | null) => {
    setDeviceState(d);
  }, []);


  const [pendingDialNumber, setPendingDialNumberState] = useState<string | null>(null);
  const setPendingDialNumber = useCallback((number: string | null) => {
    setPendingDialNumberState(number);
  }, []);

  // Same reason as selectedCallerId: a half-typed number must survive the card closing.
  const [dialerDraft, setDialerDraftState] = useState<string>("");
  const setDialerDraft = useCallback((value: string) => {
    setDialerDraftState(value);
  }, []);

  // Lives here rather than in DialerTab so the choice survives tab switches and the dialog
  // closing. Dialing from Recent Calls remounts DialerTab, which used to reset it.
  const [selectedCallerId, setSelectedCallerIdState] = useState<string>(
    () => sessionStorage.getItem("dialerCallerId") ?? "",
  );
  const setSelectedCallerId = useCallback((phoneNumberId: string) => {
    setSelectedCallerIdState(phoneNumberId);
    sessionStorage.setItem("dialerCallerId", phoneNumberId);
  }, []);

  const closeHandlerRef = useRef<(() => void) | null>(null);
  const registerCloseHandler = useCallback((fn: (() => void) | null) => {
    closeHandlerRef.current = fn;
  }, []);

  const requestCloseCallDialog = useCallback(() => {
    if (closeHandlerRef.current) {
      closeHandlerRef.current();
    } else {
      setIsCallDialogOpen(false);
      sessionStorage.setItem("callDialogOpen", "false");
    }
  }, []);

  const [onSwitchToDialerTab, setOnSwitchToDialerTab] = useState<(() => void) | null>(null);
  const registerDialerTabSwitch = useCallback((fn: () => void) => {
    setOnSwitchToDialerTab(() => fn);
  }, []);
  const switchToDialerTab = useCallback(() => {
    onSwitchToDialerTab?.();
  }, [onSwitchToDialerTab]);

  const [triggerPosition, setTriggerPosition] = useState<Position | null>(null);

  const [openRequestId, setOpenRequestId] = useState(0);

  const openCallDialog = useCallback((triggerPos?: Position) => {
    if (triggerPos) {
      setTriggerPosition(triggerPos);
    }
    setIsCallDialogOpen(true);
    setOpenRequestId((current) => current + 1);
    sessionStorage.setItem("callDialogOpen", "true");
  }, []);

  const closeCallDialog = useCallback(() => {
    setIsCallDialogOpen(false);
    sessionStorage.setItem("callDialogOpen", "false");
  }, []);

  const toggleCallDialog = useCallback(() => {
    if (isCallDialogOpen) {
      requestCloseCallDialog();
    } else {
      openCallDialog();
    }
  }, [isCallDialogOpen, requestCloseCallDialog, openCallDialog]);

  const setDialogPosition = useCallback((position: Position) => {
    setDialogPositionState(position);
    localStorage.setItem("callDialogPosition", JSON.stringify(position));
  }, []);

  const setIncomingCall = useCallback((call: Call | null, event: IncomingCallEvent | null) => {
    setIncomingCallState(call);
    setIncomingCallEventState(event);
  }, []);

  const value = useMemo(
    () => ({
      isCallDialogOpen,
      openRequestId,
      dialogPosition,
      triggerPosition,
      openCallDialog,
      closeCallDialog,
      requestCloseCallDialog,
      registerCloseHandler,
      toggleCallDialog,
      setDialogPosition,
      incomingCall,
      incomingCallEvent,
      setIncomingCall,
      activeCall,
      setActiveCall,
      activeCallInfo,
      setActiveCallInfo,
      switchToDialerTab,
      registerDialerTabSwitch,
      onSwitchToDialerTab,
      heldCall,
      setHeldCall,
      device,
      setDevice,
      pendingDialNumber,
      setPendingDialNumber,
      dialerDraft,
      setDialerDraft,
      selectedCallerId,
      setSelectedCallerId,
    }),
    [
      isCallDialogOpen,
      openRequestId,
      dialogPosition,
      triggerPosition,
      openCallDialog,
      closeCallDialog,
      requestCloseCallDialog,
      registerCloseHandler,
      toggleCallDialog,
      setDialogPosition,
      incomingCall,
      incomingCallEvent,
      setIncomingCall,
      activeCall,
      setActiveCall,
      activeCallInfo,
      setActiveCallInfo,
      switchToDialerTab,
      registerDialerTabSwitch,
      onSwitchToDialerTab,
      heldCall,
      setHeldCall,
      device,
      setDevice,
      pendingDialNumber,
      setPendingDialNumber,
      dialerDraft,
      setDialerDraft,
      selectedCallerId,
      setSelectedCallerId,
    ],
  );

  return <CallDialogContext.Provider value={value}>{children}</CallDialogContext.Provider>;
}

export function useCallDialog() {
  const context = useContext(CallDialogContext);
  if (context === undefined) {
    throw new Error("useCallDialog must be used within a CallDialogProvider");
  }
  return context;
}
